import React from 'react';

/**
 * Alert component props interface
 */
export interface AlertProps {
  /** Alert content */
  children: React.ReactNode;
  /** Visual variant of the alert */
  variant?: 'info' | 'success' | 'warning' | 'danger';
  /** Optional title for the alert */
  title?: string;
  /** Whether the alert can be dismissed */
  dismissible?: boolean;
  /** Function to call when alert is dismissed */
  onDismiss?: () => void;
  /** Optional icon to display */
  icon?: React.ReactNode;
  /** Additional CSS class names */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

/**
 * Design system colors for alert variants
 */
const VARIANT_STYLES = {
  info: {
    background: '#EFF6FF',
    border: '#BFDBFE',
    text: '#1E40AF',
    icon: '#3B82F6',
  },
  success: {
    background: '#F0FDF4',
    border: '#BBF7D0',
    text: '#166534',
    icon: '#10B981',
  },
  warning: {
    background: '#FFFBEB',
    border: '#FDE68A',
    text: '#92400E',
    icon: '#F59E0B',
  },
  danger: {
    background: '#FEF2F2',
    border: '#FECACA',
    text: '#991B1B',
    icon: '#F04438',
  },
} as const;

/**
 * Default icons for each variant
 */
const DefaultIcons = {
  info: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  success: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  warning: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18A2 2 0 003.64 21H20.36A2 2 0 0022.18 18L13.71 3.86A2 2 0 0010.29 3.86Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  danger: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

/**
 * Close icon component
 */
const CloseIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Alert component following design system standards
 * Used for displaying important messages and notifications
 * 
 * @example
 * ```tsx
 * <Alert variant="success" title="Success!">
 *   Your changes have been saved successfully.
 * </Alert>
 * 
 * <Alert variant="warning" dismissible onDismiss={handleDismiss}>
 *   Please review your input before continuing.
 * </Alert>
 * ```
 */
const Alert: React.FC<AlertProps> = ({
  children,
  variant = 'info',
  title,
  dismissible = false,
  onDismiss,
  icon,
  className,
  style,
}) => {
  const variantStyle = VARIANT_STYLES[variant];
  const displayIcon = icon || DefaultIcons[variant];

  const getAlertStyles = (): React.CSSProperties => ({
    display: 'flex',
    padding: '16px',
    borderRadius: '8px',
    border: `1px solid ${variantStyle.border}`,
    backgroundColor: variantStyle.background,
    color: variantStyle.text,
    fontSize: '14px',
    lineHeight: '1.5',
    ...style,
  });

  const getIconStyles = (): React.CSSProperties => ({
    flexShrink: 0,
    marginRight: '12px',
    color: variantStyle.icon,
    marginTop: title ? '2px' : '0',
  });

  const getContentStyles = (): React.CSSProperties => ({
    flex: 1,
    minWidth: 0,
  });

  const getTitleStyles = (): React.CSSProperties => ({
    fontWeight: 600,
    marginBottom: '4px',
    fontSize: '14px',
    color: variantStyle.text,
  });

  const getDismissButtonStyles = (): React.CSSProperties => ({
    flexShrink: 0,
    marginLeft: '12px',
    padding: '4px',
    border: 'none',
    background: 'transparent',
    color: variantStyle.text,
    cursor: 'pointer',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.7,
    transition: 'opacity 0.15s ease-in-out',
  });

  return (
    <div
      style={getAlertStyles()}
      className={className}
      role="alert"
    >
      {displayIcon && (
        <div style={getIconStyles()}>
          {displayIcon}
        </div>
      )}
      
      <div style={getContentStyles()}>
        {title && (
          <div style={getTitleStyles()}>
            {title}
          </div>
        )}
        <div>{children}</div>
      </div>
      
      {dismissible && onDismiss && (
        <button
          type="button"
          style={getDismissButtonStyles()}
          onClick={onDismiss}
          aria-label="Dismiss alert"
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.7';
          }}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
};

Alert.displayName = 'Alert';

export default Alert;